// ────────────────────────────────────────────────────────────────────────────
//  SyncError
//
//  The function reports the client's *prediction error*: the distance between
//  where we predicted we were and where the authoritative server says we were,
//  in world units. A few units is normal (collision resolution, item pickups,
//  jumppad timing); a large or growing number means the client and the server
//  disagree about the world — the exact thing a SyncError exists to catch.
//
//  The capital S is deliberate. It is a name, not a style choice.
//
//  Thank you for your service guys ;)
// ────────────────────────────────────────────────────────────────────────────

export interface SyncErrorReport {
  /** Magnitude of the last correction, in world units. */
  units: number;
  /** The last correction per axis, in world units. */
  x: number;
  y: number;
  z: number;
  /** Worst correction since the last reset, in world units. */
  peak: number;
  /** Mean correction magnitude since the last reset. */
  mean: number;
  /** Corrections observed since the last reset. */
  samples: number;
  /** `performance.now()` of the last correction, or 0 if none yet. */
  at: number;
}

let lastX = 0;
let lastY = 0;
let lastZ = 0;
let lastAt = 0;
let peak = 0;
let sum = 0;
let samples = 0;

// Sponge soaks up the spikes: an EWMA over the last corrections, plus the
// worst value in the current window. One big snap (landing, a jumppad, an item
// pickup) should not read like sustained desync.
const SPONGE_WINDOW = 64;
const SPONGE_ALPHA = 0.2;
let ema = 0;
const recent: number[] = [];

/**
 * Feed one reconciliation correction (server position minus predicted
 * position). Called from the client's reconciliation path, once per snapshot
 * that actually moved us.
 */
export function noteSyncCorrection(x: number, y: number, z: number): void {
  const units = Math.sqrt(x * x + y * y + z * z);
  lastX = x;
  lastY = y;
  lastZ = z;
  lastAt = performance.now();
  samples++;
  sum += units;
  if (units > peak) peak = units;
  ema = samples === 1 ? units : ema + SPONGE_ALPHA * (units - ema);
  recent.push(units);
  if (recent.length > SPONGE_WINDOW) recent.shift();
}

/**
 * The current sync error: last correction, per-axis breakdown, peak and mean
 * since the last reset. Public API — console `syncerror`, `diag`, and
 * `__webrace.syncError()` all read it.
 */
export function SyncError(): SyncErrorReport {
  return {
    units: Math.sqrt(lastX * lastX + lastY * lastY + lastZ * lastZ),
    x: lastX,
    y: lastY,
    z: lastZ,
    peak,
    mean: samples > 0 ? sum / samples : 0,
    samples,
    at: lastAt,
  };
}

export interface SpongeReport {
  /** Smoothed (EWMA) correction magnitude, in world units. */
  smoothed: number;
  /** Worst correction in the current window. */
  windowPeak: number;
  /** Samples currently in the window. */
  samples: number;
  /** Window size, in corrections. */
  window: number;
}

/**
 * Sponge: the smoothed view of {@link SyncError}.
 *
 * Keeps the spike of a single reconciliation from reading like sustained
 * desync — the number that "soaks up" the noise, hence the name.
 */
export function Sponge(): SpongeReport {
  return {
    smoothed: ema,
    windowPeak: recent.length > 0 ? Math.max(...recent) : 0,
    samples: recent.length,
    window: SPONGE_WINDOW,
  };
}

/** Clear the peak/mean/Sponge counters (the last correction is kept). */
export function resetSyncError(): void {
  peak = 0;
  sum = 0;
  samples = 0;
  ema = 0;
  recent.length = 0;
}
